import { Injectable } from '@angular/core';
import {
  SUBMISSION_TABLE_LAB_COLUMN_OPTIONS,
  SUBMISSION_TABLE_LAB_COLUMN_ORDER,
  SubmissionTableLabColumnKey,
  SubmissionTableLabColumnVisibility
} from './submission-table-lab.models';

export const SUBMISSION_TABLE_LAB_VISIBLE_COLUMNS_KEY = 'underwriter-intelligence:submission-table-lab-columns:v1';

type StoredSubmissionTableLabPreferencesV3 = {
  version: 3;
  visibleColumns: SubmissionTableLabColumnKey[];
  columnOrder: SubmissionTableLabColumnKey[];
};

type StoredSubmissionTableLabPreferences =
  | { version: 1; hiddenColumns: SubmissionTableLabColumnKey[] }
  | { version: 2; visibleColumns: SubmissionTableLabColumnKey[] }
  | StoredSubmissionTableLabPreferencesV3;

const LAB_COLUMN_KEYS = new Set<SubmissionTableLabColumnKey>(
  SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.map((option) => option.key)
);

const DEFAULT_LAB_COLUMN_VISIBILITY: SubmissionTableLabColumnVisibility = Object.fromEntries(
  SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.map((option) => [option.key, option.defaultSelected] as const)
) as SubmissionTableLabColumnVisibility;

@Injectable({ providedIn: 'root' })
export class SubmissionTableLabPreferencesService {
  getDefaultVisibility(): SubmissionTableLabColumnVisibility {
    return { ...DEFAULT_LAB_COLUMN_VISIBILITY };
  }

  getVisibilityForUser(userId: number | null | undefined): SubmissionTableLabColumnVisibility {
    const preferences = this.readPreferences(userId);

    if (!preferences) {
      return this.getDefaultVisibility();
    }

    if (preferences.version === 3 || preferences.version === 2) {
      const visibility = this.getEmptyVisibility();

      for (const key of preferences.visibleColumns) {
        if (LAB_COLUMN_KEYS.has(key)) {
          visibility[key] = true;
        }
      }

      return visibility;
    }

    const visibility = this.getDefaultVisibility();

    for (const key of preferences.hiddenColumns) {
      if (LAB_COLUMN_KEYS.has(key)) {
        visibility[key] = false;
      }
    }

    return visibility;
  }

  getOrderForUser(userId: number | null | undefined): SubmissionTableLabColumnKey[] {
    const preferences = this.readPreferences(userId);

    if (!preferences || preferences.version !== 3) {
      return [...SUBMISSION_TABLE_LAB_COLUMN_ORDER];
    }

    const order = preferences.columnOrder.filter((key) => LAB_COLUMN_KEYS.has(key));
    return order.length ? order : [...SUBMISSION_TABLE_LAB_COLUMN_ORDER];
  }

  saveVisibilityForUser(userId: number | null | undefined, visibility: SubmissionTableLabColumnVisibility): void {
    this.writePreferences(userId, visibility, this.getOrderForUser(userId));
  }

  saveOrderForUser(
    userId: number | null | undefined,
    visibility: SubmissionTableLabColumnVisibility,
    columnOrder: SubmissionTableLabColumnKey[]
  ): void {
    this.writePreferences(userId, visibility, columnOrder);
  }

  resetForUser(userId: number | null | undefined): void {
    if (typeof window === 'undefined' || !this.isValidUserId(userId)) {
      return;
    }

    window.localStorage.removeItem(this.getStorageKey(userId));
  }

  private readPreferences(userId: number | null | undefined): StoredSubmissionTableLabPreferences | null {
    if (typeof window === 'undefined' || !this.isValidUserId(userId)) {
      return null;
    }

    const raw = window.localStorage.getItem(this.getStorageKey(userId));

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as unknown;

      if (!this.isPreferencesValid(parsed)) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private writePreferences(
    userId: number | null | undefined,
    visibility: SubmissionTableLabColumnVisibility,
    columnOrder: SubmissionTableLabColumnKey[]
  ): void {
    if (typeof window === 'undefined' || !this.isValidUserId(userId)) {
      return;
    }

    const visibleColumns = SUBMISSION_TABLE_LAB_COLUMN_OPTIONS
      .map((option) => option.key)
      .filter((key) => visibility[key] === true);

    const payload: StoredSubmissionTableLabPreferencesV3 = {
      version: 3,
      visibleColumns,
      columnOrder: columnOrder.filter((key) => LAB_COLUMN_KEYS.has(key))
    };

    window.localStorage.setItem(this.getStorageKey(userId), JSON.stringify(payload));
  }

  private getStorageKey(userId: number): string {
    return `${SUBMISSION_TABLE_LAB_VISIBLE_COLUMNS_KEY}:${userId}`;
  }

  private getEmptyVisibility(): SubmissionTableLabColumnVisibility {
    return Object.fromEntries(
      SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.map((option) => [option.key, false] as const)
    ) as SubmissionTableLabColumnVisibility;
  }

  private isPreferencesValid(value: unknown): value is StoredSubmissionTableLabPreferences {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const record = value as Record<string, unknown>;

    if (record['version'] === 3 && Array.isArray(record['visibleColumns']) && Array.isArray(record['columnOrder'])) {
      return record['visibleColumns'].every((item) =>
        typeof item === 'string' && LAB_COLUMN_KEYS.has(item as SubmissionTableLabColumnKey)
      ) && record['columnOrder'].every((item) =>
        typeof item === 'string' && LAB_COLUMN_KEYS.has(item as SubmissionTableLabColumnKey)
      );
    }

    if (record['version'] === 2 && Array.isArray(record['visibleColumns'])) {
      return record['visibleColumns'].every((item) =>
        typeof item === 'string' && LAB_COLUMN_KEYS.has(item as SubmissionTableLabColumnKey)
      );
    }

    if (record['version'] === 1 && Array.isArray(record['hiddenColumns'])) {
      return record['hiddenColumns'].every((item) =>
        typeof item === 'string' && LAB_COLUMN_KEYS.has(item as SubmissionTableLabColumnKey)
      );
    }

    return false;
  }

  private isValidUserId(userId: number | null | undefined): userId is number {
    return typeof userId === 'number' && Number.isFinite(userId);
  }
}
